/**
 * @fileoverview usePersistedState — a `useState` backed by localStorage.
 * @module utils/use-persisted-state
 *
 * A drop-in replacement for `useState<T>` that persists the value under `key`
 * (JSON-encoded) so the choice survives reloads and remounts. Mirrors the
 * read-in-initializer / write-in-effect pattern used by `usePersistedSet`
 * and the theme switcher.
 *
 * localStorage is per-browser, so each user on their own machine gets their
 * own persisted value — exactly the "saved locally, different per user"
 * behaviour the flagged-transactions filters and table view want.
 *
 * Callers that want per-job (or per-context) state should bake the scope into
 * `key`, e.g. `flagged:filters:${jobId}`.
 */

import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

export function usePersistedState<T>(
  key: string,
  fallback: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(key);
      if (stored) {
        try {
          return JSON.parse(stored) as T;
        } catch {
          // Corrupt value — fall through to the default.
        }
      }
    }
    return fallback;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}
